import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CubeIcon,
  PencilIcon,
  PrinterIcon,
  DocumentTextIcon,
  DocumentReportIcon,
  CheckCircleIcon,
  TrashIcon,
  PlusIcon,
  MinusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MailOpenIcon,
} from '@heroicons/react/outline'
import { PackageOption } from '../RecommendedPackageSection/PackageOption'
import {
  PACKAGE_OPTIONS_IMAGES,
  PACKAGE_OPTIONS_TITLES,
  PACKAGE_SELECTION_TITLES,
  RESERVED_PACKAGE_OPTIONS,
  UNAVAILABLE_RECOMMENDED_PACKAGE_OPTIONS,
} from '../RecommendedPackageSection/constants'
import { getAvailablePackingOptions } from '../RecommendedPackageSection/utils'
import PackageSelect from '../ShippingPackageDataSection/PackageSelect'
import { ConfirmDialog } from '../../../../../components'

// Visual states:
//   A — initial: empaque not picked yet, show RecommendedPackageSection-style
//       options + PackageSelect combobox (identical to the legacy UI).
//   B — empaque picked, no items: "Escanea al menos 1 pieza" CTA.
//   C — empaque picked, with items: items list + "Cerrar caja y generar guía"
//       (+ fragile banner if applicable).
//   D — closed: collapsed summary with print/document buttons.

const FragilePill = () => {
  const { i18n } = useTranslation()
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-red-50 text-red-700">
      {i18n.t('generate_shipment.multi_box.fragile_pill')}
    </span>
  )
}

// Small inline spinner used wherever a button/control is waiting on the
// backend (scan, unscan, close, reopen, etc). Keeps a single visual
// language across the panel so operators learn what "I'm waiting" looks
// like and stop second-guessing whether a click registered.
const Spinner = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
)

// Mirrors the platform's bulk-scan quantity cap.
const BULK_ADD_MAX = 1000

export const PaqueteCard = ({
  boxNumber,
  isActive = false,
  box = null,
  empaque = null,
  productNameBySku = {},
  productFragileBySku = {},
  isFragileOrder = false,

  // For the recommendation strip + combobox (identical to legacy UI)
  storePackingOptions = null, // dict {package_key: fragile_flag}
  availableShippingPackages, // packages list for the combobox
  isPackagelessOrder = false,
  hasPackagelessProduct = false,
  orderStoreId = null,
  shouldAutofocusCombobox = false,
  autofocusComboboxSignal = 0,

  onSelectEmpaque = pkg => {},
  onCloseBox = () => {},
  onDeleteCaja = () => {},
  onRemoveOne = sku => {},
  onAddBulk = (sku, count) => {},
  canBulkAdd = false,
  onRemoveBulk = (sku, count) => {},
  onPrintBoxPackingList = () => {},
  onPrintShippingLabel = () => {},
  onPrintBoxInvoice = () => {},
  onMakeActive = () => {},
  onReopenBox = () => {},
  // The scan-focus rule lives in GenerateShipment and can only see dialogs
  // whose open state lives there. These two keep it honest about the card's
  // OWN confirms (reopen / delete): while one is open the rule must not
  // fight its focus trap, and once it has fully left the focus goes back to
  // the scan input — Headless UI would otherwise restore it to whatever
  // connected element its history holds (often the card div, or nothing).
  onCardDialogOpenChange = null,
  onRequestScanFocus = () => {},
  isLastVisibleCaja = false,
  // CUBBO-2440: terminal orders (shipped/cancelled) render this card purely to
  // display the packed cajas + their generated documents. In read-only we keep
  // the print buttons but drop every mutation (reopen, delete, close, unscan)
  // so the operator isn't invited into a flow the backend will reject anyway.
  readOnly = false,
  // When true (Scenario A — carrier mints labels per-box), closing the
  // box ALSO mints the carrier label. Drives the loading-state text so
  // the operator isn't told "Generando packing list…" while the slow
  // carrier round-trip is happening.
  carrierSupportsPerBoxLabel = false,
  // BUGS-7317: when the order has NO scannable items (e.g. a removal of
  // injertos / inserts — products with is_scannable === false), there is
  // nothing to bipar, so the "Escanea al menos 1 pieza" gate can never be
  // satisfied. In that case allow closing the box directly from State B so
  // the operator can finalize the order and generate the label.
  allowCloseWithoutItems = false,
}) => {
  const { i18n } = useTranslation()
  const tr = (k, opts) => i18n.t(`generate_shipment.multi_box.${k}`, opts)
  const piecesLabel = n => (n === 1 ? tr('pieces_one', { count: n }) : tr('pieces_other', { count: n }))
  // Inline bulk-add input state (which SKU row is in edit mode + draft value).
  const [bulkAddSku, setBulkAddSku] = useState(null)
  const [bulkAddValue, setBulkAddValue] = useState('')
  const [bulkAddInFlight, setBulkAddInFlight] = useState(false)
  // Mirror state for bulk REMOVE — operator inputs N and we unscan N items
  // for that SKU in one step (instead of clicking −1 N times).
  const [bulkRemoveSku, setBulkRemoveSku] = useState(null)
  const [bulkRemoveValue, setBulkRemoveValue] = useState('')
  const [bulkRemoveInFlight, setBulkRemoveInFlight] = useState(false)
  // Reopen a closed caja so the operator can correct mistakes
  // (over-packed, wrong empaque, etc.) before the order is finalized.
  // Confirmation goes through the project's ConfirmDialog (not window.confirm)
  // so the styling matches the rest of the app.
  const [reopeningInFlight, setReopeningInFlight] = useState(false)
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false)
  // Visible feedback for the trash button.
  const [deletingInFlight, setDeletingInFlight] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  // Report the card's own confirms upward while one is open. Cleanup (not a
  // paired false-call) so an unmount mid-confirm — the box flipping state
  // swaps the whole card branch — can never leak a permanent "dialog open"
  // block on the scan focus.
  const anyCardDialogOpen = reopenConfirmOpen || deleteConfirmOpen
  useEffect(() => {
    if (!anyCardDialogOpen || !onCardDialogOpenChange) return undefined
    onCardDialogOpenChange(true)
    return () => onCardDialogOpenChange(false)
  }, [anyCardDialogOpen, onCardDialogOpenChange])
  const doDelete = async () => {
    if (deletingInFlight) return
    // Same rationale as confirmReopen — keep the modal open with a
    // loading state so the operator sees something is happening.
    setDeletingInFlight(true)
    try {
      await onDeleteCaja()
    } finally {
      setDeletingInFlight(false)
      setDeleteConfirmOpen(false)
    }
  }
  const handleDeleteClick = () => {
    if (deletingInFlight) return
    // If the caja has items (common after a reopen), warn the operator —
    // backend releases packed_units back to PICKED stock so it's safe, but
    // it's a destructive action they should explicitly confirm. Empty
    // cajas delete with no prompt to keep that flow fast.
    if (totalInBox > 0) {
      setDeleteConfirmOpen(true)
      return
    }
    doDelete()
  }
  // Per-SKU "-1" in-flight tracker — shows a spinner on the row while the
  // unscan request is round-tripping. Without it the operator clicks "-1"
  // and the count drops instantly (optimistic) but they can't tell if the
  // backend has confirmed it. We don't disable other rows; just THIS one.
  const [removingOneSku, setRemovingOneSku] = useState(null)
  const handleRemoveOneClick = async sku => {
    if (removingOneSku) return
    setRemovingOneSku(sku)
    try {
      await onRemoveOne(sku)
    } finally {
      // Small grace so the spinner is visible even when the network
      // is fast — < 80ms feels like nothing happened. 200ms is enough
      // to register without feeling slow.
      setTimeout(() => setRemovingOneSku(null), 200)
    }
  }
  const askReopen = () => {
    if (reopeningInFlight) return
    setReopenConfirmOpen(true)
  }
  const confirmReopen = async () => {
    // Keep the dialog open while the request is in flight + flip it
    // to a "loading" state. Closing the modal before the response
    // arrives makes the operator think nothing happened — the spinner
    // on the card is often scrolled off-screen by the time they hit
    // "Sí, reabrir".
    setReopeningInFlight(true)
    try {
      await onReopenBox()
    } finally {
      setReopeningInFlight(false)
      setReopenConfirmOpen(false)
    }
  }
  // Loading state for "Cerrar caja" — the close call generates a packing
  // list (and in scenario A also mints the per-box label) so it can take
  // >1s; we disable the button + swap the label to "Generando packing
  // list…" so the operator knows it's working.
  const [closingInFlight, setClosingInFlight] = useState(false)
  // Collapsible items list — operator can hide the table to free vertical
  // space when multiple cards are open. Defaults to expanded.
  const [itemsCollapsed, setItemsCollapsed] = useState(false)
  const handleCloseClick = async () => {
    if (closingInFlight) return
    setClosingInFlight(true)
    try {
      await onCloseBox()
    } finally {
      setClosingInFlight(false)
    }
  }
  const items = box?.items || {}
  const itemEntries = Object.entries(items)
  const totalInBox = itemEntries.reduce((sum, [, n]) => sum + n, 0)
  const hasItems = totalInBox > 0
  const isClosed = !!box?.is_closed
  const hasEmpaque = !!empaque
  // Banner solo cuando ALGUNA pieza dentro de ESTA caja es frágil. El flag
  // `isFragileOrder` se mantiene para `getAvailablePackingOptions` (lista de
  // empaques disponibles) pero no dispara el banner — éste es per-caja.
  const hasFragile = itemEntries.some(([sku]) => productFragileBySku[sku])

  // Shared "Cerrar caja" button — used both in State C (empaque + items) and
  // in State B when the order has no scannable items (BUGS-7317: inserts-only
  // removals). Extracted so both entry points stay visually identical.
  const closeBoxButton = (
    <button
      type="button"
      onClick={handleCloseClick}
      disabled={closingInFlight}
      className={`w-full py-3 rounded text-white text-sm font-semibold inline-flex items-center justify-center gap-2 ${
        closingInFlight ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
      }`}
    >
      {closingInFlight && (
        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
          <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )}
      {closingInFlight ? (carrierSupportsPerBoxLabel ? tr('close_box_in_flight_label_with_label') : tr('close_box_in_flight_label')) : tr('close_box')}
    </button>
  )

  const recommendedOptions = useMemo(() => {
    if (!storePackingOptions) return []
    const excluded = [...RESERVED_PACKAGE_OPTIONS, ...UNAVAILABLE_RECOMMENDED_PACKAGE_OPTIONS]
    return getAvailablePackingOptions(storePackingOptions, isPackagelessOrder, isFragileOrder, excluded, true)
  }, [storePackingOptions, isPackagelessOrder, isFragileOrder])

  // ───── State D: closed (collapsed summary) ─────
  if (isClosed) {
    return (
      <div className="border border-gray-200 rounded-lg bg-white p-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <CubeIcon className="w-4 h-4 text-gray-400 shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-800">{tr('box_label', { n: boxNumber })}</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-green-50 text-green-700">
                {tr('box_closed_pill')} <CheckCircleIcon className="w-3 h-3" />
              </span>
            </div>
            {/* Piece count FIRST so a long empaque name can't push it into
                            the truncation "…" (CUBBO-2440): the count stays visible and
                            only the empaque name ellipsizes. */}
            <div className="text-xs text-gray-500 truncate">
              {totalInBox > 0 && <span className="font-medium text-gray-600">{piecesLabel(totalInBox)} · </span>}
              {tr('empaque_selected_prefix')} {empaque?.name || '—'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {box?.packing_list_url && (
            <button
              type="button"
              onClick={onPrintBoxPackingList}
              title={tr('print_packing_list')}
              className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
            >
              <PrinterIcon className="w-4 h-4" />
            </button>
          )}
          {/* Per-box shipping label only exists when the carrier minted it
                        at close (scenario A). In scenario B all labels come from the
                        bulk generate-labels endpoint at the end of the order, so this
                        button doesn't apply. */}
          {box?.shipping_label_url && (
            <button
              type="button"
              onClick={onPrintShippingLabel}
              title={tr('print_shipping_label')}
              className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
            >
              <DocumentTextIcon className="w-4 h-4" />
            </button>
          )}
          {/* BR / NF-e — same invoice for all boxes of the order; backend
                        echoes it on every box payload when applicable. */}
          {box?.invoice_url && (
            <button
              type="button"
              onClick={onPrintBoxInvoice}
              title={tr('print_invoice')}
              className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50"
            >
              <DocumentReportIcon className="w-4 h-4" />
            </button>
          )}
          {/* Reopen: invalidates the label + packing list and lets the
                        operator correct mistakes (wrong count, wrong empaque) on
                        a previously closed caja before the order is finalized.
                        Hidden in read-only — a shipped order can't be reopened. */}
          {!readOnly && (
            <button
              type="button"
              onClick={askReopen}
              disabled={reopeningInFlight}
              title={reopeningInFlight ? tr('reopen_box') + '…' : tr('reopen_box_hint')}
              className={`p-1.5 rounded border ${
                reopeningInFlight
                  ? 'border-amber-300 bg-amber-50 text-amber-700 cursor-wait'
                  : 'border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800 hover:border-amber-400'
              }`}
            >
              {reopeningInFlight ? <Spinner className="w-4 h-4" /> : <MailOpenIcon className="w-4 h-4" />}
            </button>
          )}
        </div>
        <ConfirmDialog
          open={reopenConfirmOpen}
          setOpen={setReopenConfirmOpen}
          title={tr('reopen_box')}
          description={tr('reopen_confirm_description', { n: boxNumber })}
          confirmLabel={tr('reopen_confirm_yes')}
          cancelLabel={tr('reopen_confirm_cancel')}
          onConfirm={confirmReopen}
          type="danger"
          loading={reopeningInFlight}
          loadingLabel={tr('reopen_in_flight')}
          afterLeave={onRequestScanFocus}
        />
      </div>
    )
  }

  const cardBorder = isActive ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'

  // Delete is allowed on any open caja except the last visible one
  // (deleting the only caja would leave the panel in an invalid empty
  // state — the operator should change the empaque instead). Cajas WITH
  // items are deletable too (common after reopen → discard); backend
  // releases packed_units to PICKED stock and we ask the operator to
  // confirm before the destructive call fires.
  const canDelete = !isLastVisibleCaja

  // Clicking the card body marks it as the active caja so subsequent
  // scans flow into it. Skip when the click landed on an interactive
  // control (button, input, form) so the operator can use pencil / trash /
  // bulk-add / close without accidentally re-selecting the caja.
  const handleCardClick = e => {
    if (isActive) return
    if (e.target.closest && e.target.closest('button, input, form, select, textarea, a')) return
    onMakeActive()
  }

  return (
    <div
      onClick={handleCardClick}
      className={`border ${cardBorder} rounded-lg bg-white p-4 ${!isActive ? 'cursor-pointer hover:border-gray-300' : ''}`}
      role="button"
      tabIndex={isActive ? -1 : 0}
      onKeyDown={e => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ') && !isActive) {
          e.preventDefault()
          onMakeActive()
        }
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-2 min-w-0">
          <span
            className={`inline-flex items-center justify-center w-9 h-9 rounded-md ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'} shrink-0`}
          >
            <CubeIcon className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800">{tr('box_label', { n: boxNumber })}</div>
            {hasEmpaque ? (
              <div className="text-xs text-gray-500">
                {tr('empaque_selected_prefix')} <span className="font-medium text-gray-700">{empaque.name}</span>
              </div>
            ) : (
              <div className="text-xs text-gray-500">{tr('no_empaque_hint')}</div>
            )}
          </div>
        </div>
        {/* Pencil is gone — the empaque combobox below is always visible
                    and editable, so a separate "Cambiar empaque" affordance is
                    redundant. The combobox itself triggers the PATCH.
                    Delete is hidden in read-only (nothing to mutate). */}
        {!readOnly && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={canDelete && !deletingInFlight ? handleDeleteClick : undefined}
              disabled={!canDelete || deletingInFlight}
              title={deletingInFlight ? tr('delete_box') + '…' : canDelete ? tr('delete_box') : tr('delete_box_disabled_last')}
              className={`p-1.5 rounded ${
                deletingInFlight
                  ? 'text-red-600 bg-red-50 cursor-wait'
                  : canDelete
                    ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                    : 'text-gray-300 cursor-not-allowed'
              }`}
            >
              {deletingInFlight ? <Spinner className="w-4 h-4" /> : <TrashIcon className="w-4 h-4" />}
            </button>
          </div>
        )}
      </div>

      {/* Empaque editor — editable while packing. Operator can swap the
                empaque at any time (backend rejects with 422 box_has_units when
                items are already packed; the error surfaces in the bottom red bar).
                Hidden in read-only: the combobox's onShippingPackageChange is the
                only path to updatePackingBoxShippingPackage (a PATCH), which a
                terminal order must not fire. The chosen empaque still shows in the
                card header ("Empaque seleccionado: …"). */}
      {!readOnly && (
        <>
          {recommendedOptions.length > 0 && (
            <div className="flex gap-2 mb-3 flex-wrap">
              {recommendedOptions.map((packageKey, index) => (
                <PackageOption
                  key={`packageOption-${packageKey}`}
                  image={PACKAGE_OPTIONS_IMAGES[packageKey]}
                  name={PACKAGE_OPTIONS_TITLES[packageKey] && tr(`package_option_titles.${packageKey}`)}
                  selectionTitle={PACKAGE_SELECTION_TITLES[index] && tr(`package_selection_titles.${index}`)}
                  isSelected={index === 0}
                />
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 mb-3">
            <label className="text-sm font-medium text-gray-700 shrink-0">{tr('empaque_used')}</label>
            <div className="flex-1">
              <PackageSelect
                isPackagelessOrder={isPackagelessOrder}
                hasPackagelessProduct={hasPackagelessProduct}
                isFragileOrder={isFragileOrder}
                storePackingOptions={storePackingOptions}
                availableShippingPackages={availableShippingPackages}
                orderStoreId={orderStoreId}
                initialShippingPackage={empaque || null}
                onShippingPackageChange={pkg => onSelectEmpaque(pkg)}
                shouldAutofocus={shouldAutofocusCombobox && !hasEmpaque}
                autofocusSignal={autofocusComboboxSignal}
              />
            </div>
          </div>
        </>
      )}

      {/* ───── State B: empaque picked, no items yet ───── */}
      {hasEmpaque && !hasItems && (
        <>
          {allowCloseWithoutItems ? (
            // BUGS-7317: inserts-only order — nothing to scan, so let the
            // operator close the box directly to finalize the order.
            <>
              <p className="text-sm text-gray-500 text-center py-2 mb-2">{tr('inserts_only_prompt')}</p>
              {!readOnly && closeBoxButton}
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 text-center py-2 mb-2">{tr('scan_prompt')}</p>
              <button type="button" disabled className="w-full py-3 rounded bg-blue-300 text-white text-sm font-medium cursor-not-allowed">
                {tr('scan_at_least_one')}
              </button>
            </>
          )}
        </>
      )}

      {/* ───── State C: empaque picked + items ───── */}
      {hasEmpaque && hasItems && (
        <>
          {hasFragile && (
            <div className="mb-3 px-3 py-2 rounded bg-red-50 border border-red-200">
              <div className="text-sm font-semibold text-red-700">{tr('fragile_banner_title')}</div>
              <div className="text-xs text-red-600">{tr('fragile_banner_subtitle')}</div>
            </div>
          )}
          <div className="mb-3">
            <div className="flex items-center justify-between pb-2">
              <div className="flex items-baseline gap-3 text-sm">
                <span className="font-semibold text-gray-800">{tr('pieces_packed')}</span>
                <span className="text-gray-500">{piecesLabel(totalInBox)}</span>
              </div>
              <button
                type="button"
                onClick={() => setItemsCollapsed(prev => !prev)}
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                {itemsCollapsed ? tr('show_more') : tr('show_less')}
                {itemsCollapsed ? <ChevronDownIcon className="w-3.5 h-3.5" /> : <ChevronUpIcon className="w-3.5 h-3.5" />}
              </button>
            </div>
            {!itemsCollapsed && (
              <>
                <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-gray-400 font-medium pb-1 border-b border-gray-100">
                  <span>SKU</span>
                  <span>{tr('quantity_header')}</span>
                </div>
                <ul className="divide-y divide-gray-100">
                  {itemEntries.map(([sku, qty]) => {
                    const isAdding = bulkAddSku === sku
                    const isRemoving = bulkRemoveSku === sku
                    const inFlight = box?.itemsInFlight?.[sku] || 0
                    const isRemovingOne = removingOneSku === sku
                    return (
                      <li
                        key={sku}
                        className={`flex items-center justify-between py-1.5 text-sm group gap-2 ${inFlight > 0 || isRemovingOne ? 'bg-blue-50/40 rounded' : ''}`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-gray-700 truncate" title={`${sku}${productNameBySku[sku] ? ` · ${productNameBySku[sku]}` : ''}`}>
                            {sku}
                          </span>
                          {productFragileBySku[sku] && <FragilePill />}
                          {/* Visible "waiting on backend" cue when there are
                                                optimistic units for this SKU that the server hasn't
                                                confirmed yet, OR when the operator just clicked -1
                                                and the unscan call is in flight. */}
                          {(inFlight > 0 || isRemovingOne) && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-blue-600">
                              <Spinner className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                        <div className="shrink-0 flex items-center gap-1">
                          {isAdding && (
                            <form
                              onSubmit={async e => {
                                e.preventDefault()
                                const n = parseInt(bulkAddValue, 10)
                                if (!Number.isFinite(n) || n <= 0 || n > BULK_ADD_MAX) return
                                setBulkAddInFlight(true)
                                try {
                                  await onAddBulk(sku, n)
                                } finally {
                                  setBulkAddInFlight(false)
                                  setBulkAddSku(null)
                                  setBulkAddValue('')
                                  onRequestScanFocus()
                                }
                              }}
                              className="flex items-center gap-1"
                            >
                              <input
                                autoFocus
                                type="number"
                                min="1"
                                max={BULK_ADD_MAX}
                                value={bulkAddValue}
                                onChange={e => setBulkAddValue(e.target.value)}
                                placeholder="N"
                                disabled={bulkAddInFlight}
                                className="w-14 px-1 py-0.5 text-xs border rounded"
                              />
                              <button
                                type="submit"
                                disabled={bulkAddInFlight}
                                className="text-xs text-indigo-600 hover:text-indigo-800 disabled:text-gray-400 px-1"
                              >
                                {bulkAddInFlight ? '…' : tr('bulk_add_submit')}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setBulkAddSku(null)
                                  setBulkAddValue('')
                                  onRequestScanFocus()
                                }}
                                disabled={bulkAddInFlight}
                                className="text-xs text-gray-400 hover:text-gray-600 px-1"
                              >
                                ×
                              </button>
                            </form>
                          )}
                          {isRemoving && (
                            <form
                              onSubmit={async e => {
                                e.preventDefault()
                                const n = parseInt(bulkRemoveValue, 10)
                                if (!Number.isFinite(n) || n <= 0) return
                                // Cap at the current quantity in this caja so we
                                // never try to unscan more than what's there.
                                const safeN = Math.min(n, qty)
                                setBulkRemoveInFlight(true)
                                try {
                                  await onRemoveBulk(sku, safeN)
                                } finally {
                                  setBulkRemoveInFlight(false)
                                  setBulkRemoveSku(null)
                                  setBulkRemoveValue('')
                                }
                              }}
                              className="flex items-center gap-1"
                            >
                              <input
                                autoFocus
                                type="number"
                                min="1"
                                max={qty}
                                value={bulkRemoveValue}
                                onChange={e => setBulkRemoveValue(e.target.value)}
                                placeholder="N"
                                disabled={bulkRemoveInFlight}
                                className="w-14 px-1 py-0.5 text-xs border rounded"
                              />
                              <button
                                type="submit"
                                disabled={bulkRemoveInFlight}
                                className="text-xs text-red-600 hover:text-red-800 disabled:text-red-400 px-1 inline-flex items-center gap-1"
                              >
                                {bulkRemoveInFlight && <Spinner className="w-3 h-3" />}
                                {bulkRemoveInFlight ? tr('bulk_remove_in_flight') : tr('bulk_remove_submit')}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setBulkRemoveSku(null)
                                  setBulkRemoveValue('')
                                }}
                                disabled={bulkRemoveInFlight}
                                className="text-xs text-gray-400 hover:text-gray-600 px-1"
                              >
                                ×
                              </button>
                            </form>
                          )}
                          {!isAdding && !isRemoving && !readOnly && (
                            <>
                              {canBulkAdd && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setBulkAddSku(sku)
                                    setBulkAddValue('')
                                  }}
                                  title={tr('bulk_add_hint')}
                                  className="text-xs text-gray-400 hover:text-indigo-600 px-1 inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <PlusIcon className="w-3 h-3" /> N
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setBulkRemoveSku(sku)
                                  setBulkRemoveValue('')
                                }}
                                title={tr('remove_many_pieces_hint')}
                                className="text-xs text-gray-400 hover:text-red-600 px-1 inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MinusIcon className="w-3 h-3" /> N
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveOneClick(sku)}
                                disabled={isRemovingOne}
                                title={tr('remove_one_piece')}
                                className={`text-xs px-1 transition-opacity inline-flex items-center gap-1 ${
                                  isRemovingOne ? 'text-red-600 opacity-100 cursor-wait' : 'text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100'
                                }`}
                              >
                                {isRemovingOne ? <Spinner className="w-3 h-3" /> : '−1'}
                              </button>
                            </>
                          )}
                          <span className="text-gray-700 ml-2 w-8 text-right">{qty}</span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </>
            )}
          </div>
          {!readOnly && closeBoxButton}
        </>
      )}
      <ConfirmDialog
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        title={tr('delete_box')}
        description={tr('delete_box_confirm_description', { count: totalInBox })}
        confirmLabel={tr('delete_box_confirm_yes')}
        cancelLabel={tr('reopen_confirm_cancel')}
        onConfirm={doDelete}
        type="danger"
        loading={deletingInFlight}
        loadingLabel={tr('delete_in_flight')}
        afterLeave={onRequestScanFocus}
      />
    </div>
  )
}

export default PaqueteCard
